import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Plus, Edit, Trash2, History, Settings } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

type BusinessRule = {
  id: number;
  name: string;
  category: string;
  description: string | null;
  ruleType: string;
  ruleValue: any;
  isEnabled: boolean;
  lastUpdated: string;
  updatedBy: number | null;
  createdAt: string;
  updatedAt: string;
};

type RuleAuditLog = {
  id: number;
  ruleId: number;
  changedBy: number;
  changedByName: string;
  action: string;
  oldValue: any;
  newValue: any;
  reason: string | null;
  timestamp: string;
};

const RULE_CATEGORIES = [
  "Access Control",
  "Data Validation",
  "Business Logic",
  "Workflow",
  "Security",
  "Performance",
  "Compliance"
];

const RULE_TYPES = [
  "boolean",
  "number",
  "string",
  "array",
  "object"
];

export default function SuperAdminBusinessRules() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [editingRule, setEditingRule] = useState<BusinessRule | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAuditDialogOpen, setIsAuditDialogOpen] = useState(false);
  const [selectedRuleForAudit, setSelectedRuleForAudit] = useState<number | null>(null);

  // Fetch business rules
  const { data: rules = [], isLoading } = useQuery<BusinessRule[]>({
    queryKey: ["/api/admin/business-rules"],
    retry: 1,
    refetchOnWindowFocus: false
  });

  // Fetch audit log for selected rule
  const { data: auditLog = [] } = useQuery<RuleAuditLog[]>({
    queryKey: ["/api/admin/business-rules", selectedRuleForAudit, "audit"],
    enabled: !!selectedRuleForAudit && isAuditDialogOpen,
    retry: 1
  });

  // Create rule mutation
  const createRuleMutation = useMutation({
    mutationFn: (data: any) => apiRequest("/api/admin/business-rules", "POST", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/business-rules"] });
      setIsCreateDialogOpen(false);
      toast({
        title: "Success",
        description: "Business rule created successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create business rule",
        variant: "destructive"
      });
    }
  });

  // Update rule mutation
  const updateRuleMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => 
      apiRequest(`/api/admin/business-rules/${id}`, "PUT", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/business-rules"] });
      setIsEditDialogOpen(false);
      setEditingRule(null);
      toast({
        title: "Success",
        description: "Business rule updated successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update business rule",
        variant: "destructive"
      });
    }
  });

  // Delete rule mutation
  const deleteRuleMutation = useMutation({
    mutationFn: (id: number) => apiRequest(`/api/admin/business-rules/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/business-rules"] });
      toast({
        title: "Success",
        description: "Business rule deleted successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete business rule",
        variant: "destructive"
      });
    }
  });

  const handleCreateRule = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    
    const ruleValue = formData.get('ruleValue') as string;
    let parsedValue: any = ruleValue;
    
    // Try to parse JSON if it looks like JSON
    if (ruleValue.startsWith('{') || ruleValue.startsWith('[')) {
      try {
        parsedValue = JSON.parse(ruleValue);
      } catch (e) {
        // Keep as string if not valid JSON
      }
    }
    
    createRuleMutation.mutate({
      name: formData.get('name'),
      category: formData.get('category'),
      description: formData.get('description') || null,
      ruleType: formData.get('ruleType'),
      ruleValue: parsedValue,
      isEnabled: formData.get('isEnabled') === 'on'
    });
  };

  const handleEditRule = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingRule) return;
    
    const formData = new FormData(event.currentTarget);
    
    const ruleValue = formData.get('ruleValue') as string;
    let parsedValue: any = ruleValue;
    
    // Try to parse JSON if it looks like JSON
    if (ruleValue.startsWith('{') || ruleValue.startsWith('[')) {
      try {
        parsedValue = JSON.parse(ruleValue);
      } catch (e) {
        // Keep as string if not valid JSON
      }
    }
    
    updateRuleMutation.mutate({
      id: editingRule.id,
      data: {
        name: formData.get('name'),
        category: formData.get('category'),
        description: formData.get('description') || null,
        ruleType: formData.get('ruleType'),
        ruleValue: parsedValue,
        isEnabled: formData.get('isEnabled') === 'on'
      }
    });
  };

  const handleDeleteRule = (id: number) => {
    if (confirm("Are you sure you want to delete this business rule? This action cannot be undone.")) {
      deleteRuleMutation.mutate(id);
    }
  };

  const handleViewAudit = (ruleId: number) => {
    setSelectedRuleForAudit(ruleId);
    setIsAuditDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Business Rules Management</h1>
        </div>
        <div className="text-center py-12">Loading business rules...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Settings className="h-8 w-8" />
            Business Rules Management
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage application business rules and access control policies. Super Admin access required.
          </p>
        </div>
        
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add New Rule
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Business Rule</DialogTitle>
              <DialogDescription>
                Add a new business rule to control application behavior
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleCreateRule} className="space-y-4">
              <div>
                <Label htmlFor="name">Rule Name</Label>
                <Input id="name" name="name" required placeholder="Enter rule name" />
              </div>
              
              <div>
                <Label htmlFor="category">Category</Label>
                <Select name="category" required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {RULE_CATEGORIES.map(category => (
                      <SelectItem key={category} value={category}>{category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="ruleType">Rule Type</Label>
                <Select name="ruleType" required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {RULE_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="ruleValue">Rule Value</Label>
                <Textarea 
                  id="ruleValue" 
                  name="ruleValue" 
                  required 
                  placeholder="Enter rule value (JSON for objects/arrays)" 
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea 
                  id="description" 
                  name="description" 
                  placeholder="Describe what this rule does" 
                  rows={2}
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch id="isEnabled" name="isEnabled" defaultChecked />
                <Label htmlFor="isEnabled">Enable this rule</Label>
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createRuleMutation.isPending}>
                  Create Rule
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Rules Grid */}
      <div className="grid gap-6">
        {rules.length === 0 ? (
          <Card className="p-8 text-center">
            <Settings className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Business Rules</h3>
            <p className="text-muted-foreground mb-4">
              No business rules have been created yet. Add your first rule to get started.
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Rule
            </Button>
          </Card>
        ) : (
          rules.map((rule: BusinessRule) => (
            <Card key={rule.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-medium">{rule.name}</h3>
                    <Badge variant={rule.isEnabled ? "default" : "secondary"}>
                      {rule.isEnabled ? "Enabled" : "Disabled"}
                    </Badge>
                    <Badge variant="outline">{rule.category}</Badge>
                    <Badge variant="outline">{rule.ruleType}</Badge>
                  </div>
                  
                  {rule.description && (
                    <p className="text-muted-foreground">{rule.description}</p>
                  )}
                  
                  <div className="font-mono text-sm bg-muted p-2 rounded">
                    {typeof rule.ruleValue === 'object' 
                      ? JSON.stringify(rule.ruleValue, null, 2) 
                      : String(rule.ruleValue)
                    }
                  </div>
                  
                  <div className="text-sm text-muted-foreground">
                    Last updated: {new Date(rule.lastUpdated).toLocaleString()}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewAudit(rule.id)}
                  >
                    <History className="h-4 w-4" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingRule(rule);
                      setIsEditDialogOpen(true);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteRule(rule.id)}
                    disabled={deleteRuleMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Business Rule</DialogTitle>
            <DialogDescription>
              Modify the business rule configuration
            </DialogDescription>
          </DialogHeader>
          
          {editingRule && (
            <form onSubmit={handleEditRule} className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Rule Name</Label>
                <Input 
                  id="edit-name" 
                  name="name" 
                  required 
                  defaultValue={editingRule.name} 
                />
              </div>
              
              <div>
                <Label htmlFor="edit-category">Category</Label>
                <Select name="category" defaultValue={editingRule.category}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RULE_CATEGORIES.map(category => (
                      <SelectItem key={category} value={category}>{category}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="edit-ruleType">Rule Type</Label>
                <Select name="ruleType" defaultValue={editingRule.ruleType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RULE_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="edit-ruleValue">Rule Value</Label>
                <Textarea 
                  id="edit-ruleValue" 
                  name="ruleValue" 
                  required 
                  defaultValue={
                    typeof editingRule.ruleValue === 'object' 
                      ? JSON.stringify(editingRule.ruleValue, null, 2)
                      : String(editingRule.ruleValue)
                  }
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea 
                  id="edit-description" 
                  name="description" 
                  defaultValue={editingRule.description || ''}
                  rows={2}
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch 
                  id="edit-isEnabled" 
                  name="isEnabled" 
                  defaultChecked={editingRule.isEnabled} 
                />
                <Label htmlFor="edit-isEnabled">Enable this rule</Label>
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setIsEditDialogOpen(false);
                    setEditingRule(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={updateRuleMutation.isPending}>
                  Update Rule
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={isAuditDialogOpen} onOpenChange={setIsAuditDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Rule Audit Log</DialogTitle>
            <DialogDescription>
              View the change history for this business rule
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-96 overflow-y-auto space-y-4">
            {auditLog.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No audit entries found for this rule
              </p>
            ) : (
              auditLog.map((entry: RuleAuditLog) => (
                <Card key={entry.id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={
                        entry.action === 'create' ? 'default' :
                        entry.action === 'update' ? 'secondary' : 'destructive'
                      }>
                        {entry.action.toUpperCase()}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        by {entry.changedByName || `User ${entry.changedBy}`}
                      </span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {new Date(entry.timestamp).toLocaleString()}
                    </span>
                  </div>
                  
                  {entry.reason && (
                    <p className="text-sm mb-2">
                      <strong>Reason:</strong> {entry.reason}
                    </p>
                  )}
                  
                  {entry.action === 'update' && entry.oldValue && entry.newValue && (
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <strong>Before:</strong>
                        <pre className="bg-muted p-2 rounded mt-1 overflow-x-auto">
                          {JSON.stringify(entry.oldValue, null, 2)}
                        </pre>
                      </div>
                      <div>
                        <strong>After:</strong>
                        <pre className="bg-muted p-2 rounded mt-1 overflow-x-auto">
                          {JSON.stringify(entry.newValue, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
